import { useMutation, useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { sortBy } from 'lodash'
import { useEffect } from 'react'

import { useSelectedIds } from '@/components/big-filter/useSelectedIds'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_POS_CATEGORIES,
  GET_TENANT_ARRAY_SETTINGS,
  GetPosCategoriesQuery,
  GetPosCategoriesQueryVariables,
  getTenantArraySettingsQuery,
  getTenantArraySettingsQueryVariables,
  UPDATE_TENANT_ARRAY_SETTINGS,
  UpdateTenantArraySettingsMutation,
  UpdateTenantArraySettingsMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

export type SalesCategorySetting =
  | 'inside_sales_categories'
  | 'outside_sales_categories'
  | 'fuel_sales_categories'

export const SalesCategorySettingsMap: {
  [key in string]: SalesCategorySetting
} = {
  insideSalesCategories: 'inside_sales_categories',
  outsideSalesCategories: 'outside_sales_categories',
  fuelSalesCategories: 'fuel_sales_categories',
}

type Props = {
  open: boolean
  onClose: () => void
  setting?: SalesCategorySetting | null
}

const SalesCategorySettings: React.FC<Props> = props => {
  const { t } = useT()
  const { open, onClose, setting } = props
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const [selectedCategories, onCategorySelected, setSelectedCategories] =
    useSelectedIds()

  const { data, loading } = useQuery<
    GetPosCategoriesQuery,
    GetPosCategoriesQueryVariables
  >(GET_POS_CATEGORIES, {
    variables: { pagination: { limit: 100, offset: 0 } },
  })
  const {
    data: tenantSettingsData,
    loading: tenantSettingsLoading,
    refetch,
  } = useQuery<
    getTenantArraySettingsQuery,
    getTenantArraySettingsQueryVariables
  >(GET_TENANT_ARRAY_SETTINGS)
  const [updateTenantArraySettings, { loading: saving }] = useMutation<
    UpdateTenantArraySettingsMutation,
    UpdateTenantArraySettingsMutationVariables
  >(UPDATE_TENANT_ARRAY_SETTINGS)

  useRefresh(refetch)

  const possibleCategories =
    data?.posCategories.results.map(category => category.category) || []
  const possibleCategoriesSorted = sortBy(possibleCategories)

  const handleSelectAll = () => {
    const visible = possibleCategoriesSorted
    const visibleSelected = selectedCategories.filter(id =>
      visible.includes(id),
    )
    if (visibleSelected.length === visible.length) {
      setSelectedCategories(
        selectedCategories.filter(id => !visible.includes(id)),
      )
    } else {
      const union = Array.from(new Set([...selectedCategories, ...visible]))
      setSelectedCategories(union)
    }
  }

  const handleSave = async () => {
    if (!setting) {
      console.error('No setting provided')
      return
    }
    try {
      await updateTenantArraySettings({
        variables: {
          data: {
            settings: [{ fieldName: setting, fieldValue: selectedCategories }],
          },
        },
      })
      refetch()
      onClose()
    } catch (error) {
      console.error('Error updating settings:', error)
    }
  }

  useEffect(() => {
    if (tenantSettingsData) {
      const settings = tenantSettingsData.tenantArraySettings
      const selected =
        setting === 'inside_sales_categories'
          ? settings.insideSalesCategories
          : setting === 'outside_sales_categories'
            ? settings.outsideSalesCategories
            : settings.fuelSalesCategories
      setSelectedCategories(selected)
    }
  }, [tenantSettingsData, setting])

  const selectedSet = new Set(selectedCategories)
  const visibleSelectedCount = possibleCategoriesSorted.filter(id =>
    selectedSet.has(id),
  ).length

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle id="sales-category-settings-dialog">
        {t('salesCategorySetting.title', {
          setting: t('salesCategorySetting.setting', { setting }),
        })}
      </DialogTitle>
      <DialogContent>
        {tenantSettingsLoading || loading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : possibleCategoriesSorted.length ? (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Checkbox
                  checked={
                    possibleCategoriesSorted.length > 0 &&
                    visibleSelectedCount === possibleCategoriesSorted.length
                  }
                  indeterminate={
                    visibleSelectedCount > 0 &&
                    visibleSelectedCount < possibleCategoriesSorted.length
                  }
                  onChange={handleSelectAll}
                  inputProps={{ 'aria-label': t('common.selectAll') }}
                />
                <Typography variant="body1" fontWeight="medium">
                  {t('common.selectAll')}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('salesCategorySetting.selectedCount', {
                  selected: visibleSelectedCount,
                  total: possibleCategoriesSorted.length,
                })}
              </Typography>
            </Stack>
            {possibleCategoriesSorted.map(category => (
              <Stack
                key={category}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Checkbox
                  checked={selectedCategories.includes(category)}
                  onChange={() => {
                    onCategorySelected(category)
                  }}
                />
                <Typography variant="body1">{category}</Typography>
              </Stack>
            ))}
          </>
        ) : (
          <Stack>
            <Typography variant="body1">
              {t('salesCategorySetting.noCategories')}
            </Typography>
            <Link
              onClick={() => handleNavTo('admin/sales-data')}
              sx={{ cursor: 'pointer' }}
            >
              {t('salesCategorySetting.setupData')}
            </Link>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Stack direction={'row'} spacing={2} sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || loading || tenantSettingsLoading}
          >
            {t('common.save')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default SalesCategorySettings
